import type { CarePlan, Reference } from '@medplum/fhirtypes'
import { Intervention } from '@seen/core/models/care-plans/intervention'
import { cn } from '@seen/ui'
import type { JSX } from 'react'
import { useState } from 'react'
import { IconInterventionStatus } from '#src/components/icons/IconInterventionStatus.tsx'
import { InterventionSheet } from '#src/components/InterventionSheet.tsx'
import { useRead } from '#src/hooks/useRead.ts'
import { interventionLabel } from '#src/utils/interventionLabel.ts'

/* The sibling mention chips carry a Tabler icon, whose glyph occupies about
 * three quarters of its box, and sit it on `size-[1em] align-[-0.25ex]`.
 * `IconIntervention` is one of ours and draws edge to edge — 14 of its 16
 * viewBox units — so at the same box it renders a seventh larger and reads as
 * riding high above the text. Same technique, scaled so the glyph itself
 * matches: 0.875em box × 0.875 glyph ≈ the 0.75em the Tabler chips draw, with
 * the baseline offset halved to keep the glyph's centre where theirs sits. */
const ICON_CLASS = 'inline-block size-[0.875em] align-[-0.125ex]'

export function InterventionMention({
  intervention: interventionRef,
  className,
  displayOverride,
}: {
  intervention: Reference<CarePlan>
  className?: string
  displayOverride?: string | null
}): JSX.Element {
  const [sheetOpen, setSheetOpen] = useState(false)

  const { data: carePlan } = useRead(Intervention, interventionRef)

  /* The reference only tells us this is a CarePlan. Whether it is an
   * INTERVENTION is a question about the resource — its category — and the
   * model owns that question. A goal or initial care plan reaches this chip
   * through the same `CarePlan/<id>` reference (a pasted URL, a legacy node)
   * and must not be presented as an intervention: no status glyph, and the
   * plan's own title rather than an intervention label. */
  const intervention =
    carePlan && Intervention.isInstance(carePlan) ? carePlan : undefined

  return (
    <>
      <button
        type="button"
        onClick={() => setSheetOpen(true)}
        className={cn(
          'inline select-none rounded-sm border-[0.5px] border-gray-450 bg-gray-470 box-decoration-clone px-1 py-px text-left hover:bg-gray-460 group-[.ProseMirror-selectednode]:bg-[lch(53_52.26_286.91/0.4)]',
          className
        )}
        tabIndex={-1}
      >
        {/* Shown while the read is in flight too, so the chip does not jump as
            it resolves; withheld only once the resource is known not to be an
            intervention. */}
        {(!carePlan || intervention) && (
          <span>
            <IconInterventionStatus
              status={intervention?.status ?? 'active'}
              className={ICON_CLASS}
            />
          </span>
        )}
        {/* Derived from the live resource, never from the node's stored
            `display`: that round-trips as the anchor's own text and carries the
            `@` the picker writes in front of every mention. */}
        <span>
          {' '}
          {displayOverride ||
            (intervention ? interventionLabel(intervention) : carePlan?.title)}
        </span>
      </button>
      <InterventionSheet
        intervention={intervention}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
      />
    </>
  )
}
